import * as XLSX from 'xlsx'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import AddDeviceForm from './MappingForms/AddDeviceForm'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined'
import React, { useMemo, useCallback, useRef, useState } from 'react'
import { saveAs } from 'file-saver'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, GridReadyEvent } from 'ag-grid-community'
import {
  constants,
  DEVICE_TYPE_OPTIONS,
  ProrithmSocketMessage,
} from '../../features/ProRithm/Constants'
import {
  Box,
  Select,
  Tooltip,
  useTheme,
  MenuItem,
  IconButton,
  InputLabel,
  FormControl,
  TablePagination,
} from '@mui/material'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'

const styles = () => ({
  MedAgDataGridContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  MedAgDataGridQuickFilterContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  MedAgDataGridInput: {
    border: '1px solid grey',
    borderRadius: 5,
    padding: '5px !important',
  },
  MedAgDataGridExportIcon: {
    background: '#4682b4',
    color: '#ffffff',
    borderRadius: 5,
  },
  MedAgDataGridFilterContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
  },
  MedAgDataGridheading: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  MedAgDataGridPaginationContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'center',
    fontSize: '12px !important',
    background: '#fff',
    borderBottom: '1px solid #babfc7',
    borderLeft: '1px solid #babfc7',
    borderRight: '1px solid #babfc7',
    height: '35px',
  },
  SelectLayout: {
    height: '33px',
  },
  LightBlue: {
    background: '#4682b4',
  },
  DeviceName: {
    textDecoration: 'underline',
    fontSize: '15px',
    cursor: 'pointer',
  },
  SearchBar: {
    border: '1px solid grey',
    borderRadius: 5,
    padding: '5px',
    width: '200px',
    height: '32px',
  },
  deviceType: {
    minWidth: 150,
    height: '31px',
    '& .MuiInputBase-root': {
      height: '31px',
    },
    '& .MuiInputLabel-root': {
      top: '-4px',
    },
    '& .MuiInputLabel-shrink': {
      top: '0px',
    },
  },
})

interface DataGridProps<T> {
  tableHeight: any
  rowData: T[]
  columnDefs: ColDef[]
  pagination: boolean
  pageSizeOptions?: number[]
  defaultPageSize?: number
  page?: number
  setPage?: (page: number) => void
  pageSize?: number
  setPageSize?: (pageSize: number) => void
  totalRows?: number
  headerHeight?: number
  tableHeading?: string
  filterActions?: React.ReactNode
  searchFieldWidth?: string | number
  searchFieldPlaceHolder?: string
  searchField?: boolean
  exportIcon?: boolean
  sendMessageOnSocket: (payload: ProrithmSocketMessage) => void
  deviceTypeFilter?: string
  setDeviceTypeFilter?: (deviceType: string) => void
  [key: string]: any
}

const ProRithmAgDataGrid = <T extends object>({
  tableHeight,
  rowData,
  columnDefs,
  pagination,
  pageSizeOptions = [20, 40, 60],
  page = 0,
  setPage,
  pageSize = 20,
  setPageSize,
  totalRows = 0,
  headerHeight,
  tableHeading,
  searchFieldWidth,
  searchFieldPlaceHolder,
  searchField = true,
  exportIcon = true,
  sendMessageOnSocket,
  deviceTypeFilter = '',
  setDeviceTypeFilter,
  ...rest
}: DataGridProps<T>) => {
  const [openAddDeviceDialog, setOpenAddDeviceDialog] = useState<boolean>(false)
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const gridRef = useRef<AgGridReact<T>>(null)
  const defaultColDef = useMemo<ColDef>(
    () => ({
      //   floatingFilter: true,
      resizable: true,
      //   autoHeaderHeight: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit()
  }, [])

  const onFilterTextBoxChanged = useCallback(() => {
    const gridApi = gridRef?.current?.api
    const filterTextBoxValue = (
      document.getElementById('filter-text-box') as HTMLInputElement
    )?.value

    if (gridApi && filterTextBoxValue !== undefined) {
      gridApi.setGridOption('quickFilterText', filterTextBoxValue)
    }
  }, [])

  const exportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(rowData)
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] }
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    })

    saveAsExcelFile(excelBuffer, 'Data')
  }

  const saveAsExcelFile = (buffer: any, fileName: string) => {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    const EXCEL_EXTENSION = '.xlsx'
    const data = new Blob([buffer], { type: EXCEL_TYPE })
    saveAs(data, `${fileName}_export_${new Date().getTime()}${EXCEL_EXTENSION}`)
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    if (setPage) setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (setPage) setPage(0)
    if (setPageSize) setPageSize(parseInt(event.target.value))
  }

  return (
    <Box
      className={classes?.MedAgDataGridContainer}
      height={tableHeight}
      width='100%'
    >
      <div className={classes?.MedAgDataGridQuickFilterContainer}>
        <div className={classes?.MedAgDataGridheading} id='data-grid-heading'>
          {tableHeading}
        </div>
        <div className={classes?.MedAgDataGridFilterContainer}>
          <FormControl size='small' className={classes?.deviceType}>
            <InputLabel size='small' id='device-type-filter-label'>
              {constants?.deviceType}
            </InputLabel>
            <Select
              size='small'
              id='device-type-filter'
              value={deviceTypeFilter}
              label={constants?.deviceType}
              className={classes?.deviceType}
              labelId='device-type-filter-label'
              onChange={(e) => setDeviceTypeFilter?.(e.target.value as string)}
              endAdornment={
                deviceTypeFilter ? (
                  <Box marginRight={2}>
                    <IconButton
                      size='small'
                      onClick={() => setDeviceTypeFilter?.('')}
                    >
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </Box>
                ) : null
              }
            >
              {DEVICE_TYPE_OPTIONS?.map((option) => (
                <MenuItem key={option?.value} value={option?.value}>
                  {option?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size='small'
            id='add-device-btn'
            variant='contained'
            endIcon={<AddIcon />}
            className={classes?.LightBlue}
            onClick={() => setOpenAddDeviceDialog(true)}
          >
            {constants?.addDevice}
          </Button>
          {exportIcon ? (
            <IconButton id='export-excel-btn' onClick={exportExcel}>
              <Tooltip title='Export'>
                <GetAppOutlinedIcon
                  className={classes?.MedAgDataGridExportIcon}
                />
              </Tooltip>
            </IconButton>
          ) : null}
          {searchField ? (
            <input
              type='text'
              id='filter-text-box'
              placeholder={searchFieldPlaceHolder ?? 'Search...'}
              onInput={onFilterTextBoxChanged}
              className={classes?.MedAgDataGridInput}
              style={{ width: searchFieldWidth }}
            />
          ) : null}
        </div>
      </div>
      <Box
        className='ag-theme-alpine'
        id='ag-grid-container'
        height='100%'
        width='100%'
      >
        <AgGridReact
          ref={gridRef}
          headerHeight={headerHeight}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={false}
          onGridReady={onGridReady}
          {...rest}
        />
        {pagination && (
          <div
            className={classes?.MedAgDataGridPaginationContainer}
            id='pagination-container'
          >
            <TablePagination
              variant='footer'
              size='small'
              component='div'
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={pageSizeOptions}
            />
          </div>
        )}
      </Box>
      <AddDeviceForm
        open={openAddDeviceDialog}
        setOpen={setOpenAddDeviceDialog}
        sendMessageOnSocket={sendMessageOnSocket}
      />
    </Box>
  )
}

export default ProRithmAgDataGrid
